import React, {useState} from "react";
import PropTypes from "prop-types";
import DynamicAlert from "../../helpers/dynamicAlert";
import {authService} from "../../service/auth/auth.service";
import {primaryButtonStyle} from "./LookupSearch";

const EMPTY_REPORT = {remarks: "", action: ""};
const SUCCESS_MESSAGE = "Report submitted successfully!";
const FAILURE_MESSAGE = "Failed to submit report.";

const ReadOnlyField = ({id, label, value}) => (
    <div className="mb-3">
        <label htmlFor={id} className="form-label">{label}</label>
        <input id={id} type="text" className="form-control" value={value || ""} disabled/>
    </div>
);

ReadOnlyField.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

ReadOnlyField.defaultProps = {
    value: ""
};

// Kept mounted while hidden so typed remarks survive closing and reopening the modal.
const ReportModal = ({show, onClose, user, loyaltyId, showAlert}) => {
    const [reportForm, setReportForm] = useState(EMPTY_REPORT);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reportAlert, setReportAlert] = useState(null);

    if (!show) {
        return null;
    }

    const handleFieldChange = (e) => {
        const {name, value} = e.target;
        setReportForm((prev) => ({...prev, [name]: value}));
    };

    const handleClose = () => {
        onClose();
        setReportAlert(null);
    };

    const handleFailure = (errorMsg) => {
        showAlert(errorMsg, "danger");
        setReportAlert({id: Date.now(), type: "danger", msg: errorMsg});
    };

    const handleSuccess = () => {
        showAlert(SUCCESS_MESSAGE, "success");
        setReportAlert({id: Date.now(), type: "success", msg: SUCCESS_MESSAGE});
        setReportForm(EMPTY_REPORT);
        setTimeout(handleClose, 1500);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setReportAlert(null);

        authService.reportDiscrepancy({
            customerId: user?.customerId || "",
            email: user?.email || "",
            phoneNo: user?.phoneNo || "",
            loyaltyId: loyaltyId || "",
            remarks: reportForm.remarks,
            action: reportForm.action
        }).then(data => {
            setIsSubmitting(false);
            if (data && !data.error) {
                handleSuccess();
            } else {
                handleFailure(data?.error || FAILURE_MESSAGE);
            }
        }, error => {
            setIsSubmitting(false);
            handleFailure(error?.message || FAILURE_MESSAGE);
        });
    };

    return (
        <div className="modal-overlay">
            <div className="modal-box" style={{width: "480px", height: "auto"}}>
                <div className="modal-title">
                    <h2 id="report-modal-title">Report Discrepancy</h2>
                    <button type="button"
                            className="close-btn"
                            onClick={handleClose}
                            aria-label="Close Report modal">
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{padding: "20px 28px"}}>
                    <DynamicAlert alert={reportAlert}/>

                    <ReadOnlyField id="report-customerId" label="Customer ID" value={user?.customerId}/>
                    <ReadOnlyField id="report-email" label="Email" value={user?.email}/>
                    <ReadOnlyField id="report-phoneNumber" label="Phone Number" value={user?.phoneNo}/>
                    <ReadOnlyField id="report-loyaltyId" label="Loyalty ID" value={loyaltyId}/>

                    <div className="mb-3">
                        <label htmlFor="report-remarks" className="form-label">Remarks</label>
                        <textarea id="report-remarks"
                                  name="remarks"
                                  className="form-control"
                                  rows={3}
                                  value={reportForm.remarks}
                                  onChange={handleFieldChange}
                                  required/>
                    </div>

                    <div className="mb-3">
                        <label htmlFor="report-action" className="form-label">Action</label>
                        <textarea id="report-action"
                                  name="action"
                                  className="form-control"
                                  rows={2}
                                  value={reportForm.action}
                                  onChange={handleFieldChange}
                                  required/>
                    </div>

                    <button type="submit" id="sub_btn" style={primaryButtonStyle} disabled={isSubmitting}>
                        Submit
                        {isSubmitting && <i className="fa fa-spinner fa-spin ms-2"/>}
                    </button>
                </form>
            </div>
        </div>
    );
};

ReportModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    user: PropTypes.shape({
        customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        phoneNo: PropTypes.string
    }),
    loyaltyId: PropTypes.string,
    showAlert: PropTypes.func.isRequired
};

ReportModal.defaultProps = {
    user: null,
    loyaltyId: ""
};

export default ReportModal;
